import { GRID_CHECKBOX_SELECTION_COL_DEF, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium';
import { Alert, Tooltip } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import BlockIcon from '@mui/icons-material/Block';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { tableSettingsProps, useTableSettings, useInitialTableSettings, useCompleteTableSettings } from '../tableSettings/useTableSettings';
import tableHelper from 'src/utils/tableHelper';
import { useGetDispatchOrdersByWarehouse, useGetDispatchOrderByWarehouse, useGetInactiveDispatchOrdersByWarehouse } from '../../api/requestConfigs/warehouse';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import { PickingListPrintJob, PickingListPrintResult, useDispatchPrintingSettings } from 'src/components/organisms/printing/PrintingManager';

export const hasPickingListId = (row: { PickingListId?: string | number | null }) =>
  row.PickingListId != null && String(row.PickingListId).trim() !== '';

const isDispatchOrderSelectable = (params: GridRowParams) => hasPickingListId(params.row);

const dispatchOrderCheckboxColumn: GridColDef = {
  ...GRID_CHECKBOX_SELECTION_COL_DEF,
  hideable: false,
  renderCell: (params) => (
    <Tooltip title={hasPickingListId(params.row) ? '' : 'Print this order’s picking list before selecting it for a collective list.'} arrow>
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
        {GRID_CHECKBOX_SELECTION_COL_DEF.renderCell?.(params)}
      </span>
    </Tooltip>
  ),
};

// Render printingContent alongside the grid, outside its virtualized cells.
export const useDispatchOrdersTable = (
  warehouseId: number | string | null,
  refreshKey?: unknown,
  includeArchived = false,
): [columns: GridColDef[], tableSettings: tableSettingsProps, printingContent: React.ReactNode, activeRows: any[]] => {
  const { rows: activeRows, status: activeStatus } = useGetDispatchOrdersByWarehouse(warehouseId, refreshKey);
  const { rows: archivedRows, status: archivedStatus } = useGetInactiveDispatchOrdersByWarehouse(warehouseId, refreshKey, includeArchived);
  const rows = React.useMemo(() => includeArchived ? tableHelper.mergeById(activeRows ?? [], archivedRows ?? [], 'DispatchOrderId') : activeRows, [includeArchived, activeRows, archivedRows]);
  const status = includeArchived ? (archivedStatus === 'success' ? activeStatus : archivedStatus) : activeStatus;
  const idPath = ['DispatchOrderId'];
  const printingSettings = useDispatchPrintingSettings();
  const [printJob, setPrintJob] = useState<{
    key: number;
    orderId: number | string;
    warehouseId: number | string;
    settings: typeof printingSettings;
  } | null>(null);
  const printSequence = useRef(0);
  const printingRef = useRef(false);
  const [printResult, setPrintResult] = useState<PickingListPrintResult | null>(null);

  const startPrint = useCallback((orderId: number | string) => {
    if (warehouseId == null || orderId == null || printingRef.current) return;
    printingRef.current = true;
    setPrintResult(null);
    setPrintJob({ key: ++printSequence.current, orderId, warehouseId, settings: printingSettings });
  }, [warehouseId, printingSettings]);

  const finishPrint = useCallback((result: PickingListPrintResult) => {
    printingRef.current = false;
    setPrintResult(result);
    setPrintJob(null);
  }, []);

  const columns: GridColDef[] = React.useMemo(
    () => [
      dispatchOrderCheckboxColumn,
      { field: 'actions', getActions: (params) => tableHelper.buttonColumn(params, 'Edit', '/warehouse/dispatch-orders/edit?id=', 'DispatchOrderId', 'Edit Dispatch Order'), type: 'actions', headerName: 'Edit', minWidth: 80, maxWidth: 120, flex: 0.8 },
      { field: 'DispatchOrderId', headerName: 'Dispatch Order Id' },
      { field: 'ExternalOrderId', headerName: 'External Order Id' },
      {
        field: 'PickingListId', headerName: 'Picking List', minWidth: 220,
        renderCell: (params: GridRenderCellParams) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CustomButton title={printJob?.orderId === params.row.DispatchOrderId ? 'Printing...' : 'Print picking list'} size="small" iconButton={true} variant="outlined" icon={<PrintIcon />} disabled={printJob != null || warehouseId == null || params.row.DispatchOrderId == null} onClick={(event) => {
              event.stopPropagation();
              startPrint(params.row.DispatchOrderId);
            }} />
            <span>{params.row.PickingListId ?? '-'}</span>
          </div>
        ),
      },
      { field: 'Receiver', headerName: 'Receiver', flex: 1 },
      { field: 'OrderStatus_Value', headerName: 'Status', valueGetter: tableHelper.getFieldKeyOrValue, renderCell: tableHelper.renderStatusCell },
      { field: 'blocked', headerName: 'Blocked', renderCell: (params: GridRenderCellParams) => params.row.DispatchOrderBlockId != null ? <Tooltip title={params.row.DispatchOrderBlockReason || 'Blocked'} arrow><BlockIcon color="error" fontSize="small" /></Tooltip> : null },
      { field: 'Priority_Value', headerName: 'Priority', valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'DeliveryMethod_Value', headerName: 'Delivery Method', valueGetter: tableHelper.getFieldKeyOrValue, flex: 1 },
      { field: 'DispatchOrderType_Value', headerName: 'Order Type', valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'CreateDate', headerName: 'Create Date', type: 'dateTime', valueGetter: tableHelper.getDateColumn },
      { field: 'DispatchDate', headerName: 'Dispatch Date', type: 'dateTime', valueGetter: tableHelper.getDateColumn },
      { field: 'StreetAddress', headerName: 'Street Address', flex: 1 },
      { field: 'PostalCode', headerName: 'Postal Code' },
      { field: 'City', headerName: 'City' },
      { field: 'Country', headerName: 'Country' },
      { field: 'ExternalWarehouseStatus', headerName: 'Warehouse Status' },
      { field: 'DispatchOrderBlockReason', headerName: 'Block Reason' },
      { field: 'WarehouseId', headerName: 'Warehouse Id' },
      { field: 'WebOrderId', headerName: 'Web Order Id' },
      { field: 'DispatchOrderBlockId', headerName: 'Block Id' },
      { field: 'OrderStatus_Key', headerName: 'Status Id', valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'Priority_Key', headerName: 'Priority Id', valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'DeliveryMethod_Key', headerName: 'Delivery Method Id', valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'DispatchOrderType_Key', headerName: 'Order Type Id', valueGetter: tableHelper.getFieldKeyOrValue },
    ],
    [printJob, warehouseId, startPrint],
  );

  const tableName = 'DispatchOrdersTable';
  const tableSettings = Object.assign(useTableSettings({ columns, idPath, rows, tableName, status }), {
    checkboxSelection: true,
    isRowSelectable: isDispatchOrderSelectable,
  });

  useEffect(() => {
    tableSettings.setColumnVisibilityModel({
      WarehouseId: false,
      WebOrderId: false,
      DispatchOrderBlockId: false,
      OrderStatus_Key: false,
      Priority_Key: false,
      DeliveryMethod_Key: false,
      DispatchOrderType_Key: false,
      DispatchDate: false,
      StreetAddress: false,
      PostalCode: false,
      Country: false,
      ExternalWarehouseStatus: false,
      DispatchOrderBlockReason: false,
    });
  }, []);

  const notice = printResult?.error ?? (printResult?.outcome && printResult.outcome.method !== 'printer'
    ? `${printResult.outcome.method === 'savePdf' ? 'PDF download requested.' : 'Printing skipped.'}${printResult.outcome.reason ? ` ${printResult.outcome.reason}` : ''}`
    : null);
  const printingContent = <>
    {printJob && <PickingListPrintJob key={printJob.key} orderId={printJob.orderId} warehouseId={printJob.warehouseId} settings={printJob.settings} onComplete={finishPrint} />}
    {notice && <Alert severity={printResult?.error ? 'error' : 'warning'} onClose={() => setPrintResult(null)} sx={{ mb: 2 }}>{notice}</Alert>}
  </>;

  return [columns, tableSettings, printingContent, activeRows];
};

export const useDispatchOrderRowsTable = (DispatchOrderId): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const { rows: dispatchOrder, status }: any = useGetDispatchOrderByWarehouse(DispatchOrderId, 12);
  const rows = dispatchOrder?.DispatchOrderRows || [];
  const idPath = ['DispatchOrderRowId'];

  const columns: GridColDef[] = React.useMemo(
    () => [
      { field: 'DispatchOrderRowId', headerName: 'Row Id' },
      { field: 'ProductId', headerName: 'Product Id' },

      { field: 'Product', headerName: 'Product', flex: 1 },
      { field: 'ManufacturerProductId', headerName: 'Manufacturer Product Id', width: 200 },
      { field: 'BarCode', headerName: 'Barcode(EAN)', width: 200},
      { field: 'OrderedQty', headerName: 'Ordered Qty' },
      { field: 'DispatchedQty', headerName: 'Dispatched Qty', editable: true },

      { field: 'ExternalDocumentRowId', headerName: 'External Document Row Id' },
    ],
    [],
  );

  const tableName = 'DispatchOrderRowsTable';
  const tableSettings = useTableSettings({ columns, idPath, rows, tableName, status });
  tableSettings.hasToolbar = false;
  tableSettings.autoHeight = true;
  tableSettings.hideFooter = true;
  tableSettings.loading=false;

  return [columns, tableSettings];
};

// Both API response formats are normalized before they reach the grid.
export const normalizeDispatchOrderShipments = (shipments: any): any[] => Array.isArray(shipments) ? shipments.map((shipment) => ({
  ...shipment,
  CreateDate: shipment.CreateDate ?? shipment.CreatedDate ?? null,
  ShipmentPackages: Array.isArray(shipment.ShipmentPackages) ? shipment.ShipmentPackages : [],
})) : [];

export const applyDispatchOrderShipmentResult = (result: any, previousShipments: any[] = []) => {
  const order = result.DispatchOrder;
  const shipments = normalizeDispatchOrderShipments(order.Shipments ?? previousShipments);
  const details = result.ShipmentDetails;
  if (!details) return { ...order, Shipments: shipments };

  const [shipment] = normalizeDispatchOrderShipments([details]);
  const index = shipments.findIndex((item) => item.ShipmentId != null && shipment.ShipmentId != null && String(item.ShipmentId) === String(shipment.ShipmentId));
  if (index === -1) shipments.push(shipment);
  else shipments[index] = { ...shipments[index], ...shipment };
  return { ...order, Shipments: shipments };
};

const shipmentFields = ['ShipmentId', 'OrderDocumentTypeId', 'OrderDocumentTypeName', 'Comment', 'ShippingMethodId', 'ShippingMethodName', 'DispatchOrderId', 'OrderId', 'ExternalShipmentId', 'CreateDate', 'CommercialInvoiceId', 'DropShipmentPurchaseOrderId'];
const addressFields = ['Id', 'Company', 'Department', 'Title', 'Salutation', 'FirstName', 'MiddleNameInitial', 'LastName', 'Street', 'StreetAdditionalInformation', 'Postcode', 'City', 'County', 'State', 'Country_Key', 'Country_Value', 'AdditionalInformation', 'DoorCode', 'Contact', 'EmailAddress', 'PhoneNumber', 'MobileNumber', 'FaxNumber', 'WebAddress'];
const packageFields = ['Id', 'ShipmentId', 'Weight', 'Length', 'Width', 'Height', 'Volume', 'DimensionUnitId', 'WeightUnitId', 'VolumeUnitId', 'TrackingNumber', 'ReturnLabelTrackingNumber', 'ShippingLabelHref', 'ShippingReturnLabelHref'];
const fieldLabel = (field: string) => field.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
const idPath = ['ShipmentPackageRowId'];

export const getDispatchOrderShipmentRows = (shipments: any): any[] => normalizeDispatchOrderShipments(shipments).flatMap((shipment, shipmentIndex) => {
  const shipmentRow: Record<string, any> = {};
  shipmentFields.forEach((field) => { shipmentRow[field] = shipment[field] ?? null; });
  const createDate = shipment.CreateDate == null ? null : new Date(shipment.CreateDate);
  shipmentRow.CreateDate = createDate && !Number.isNaN(createDate.getTime()) ? createDate : null;
  ['AddressFrom', 'AddressTo'].forEach((prefix) => {
    const address = shipment[prefix];
    addressFields.forEach((field) => {
      const value = field === 'Country_Key' ? address?.Country?.Key : field === 'Country_Value' ? address?.Country?.Value : address?.[field];
      shipmentRow[`${prefix}_${field}`] = value ?? null;
    });
  });

  // Keep a shipment visible even when it has no packages or package IDs yet.
  const packages = shipment.ShipmentPackages.length ? shipment.ShipmentPackages : [null];
  return packages.map((shipmentPackage, packageIndex) => {
    const row: Record<string, any> = { ...shipmentRow, ShipmentPackageCount: shipment.ShipmentPackages.length, ShipmentPackageRowId: JSON.stringify([shipment.ShipmentId ?? shipmentIndex, shipmentPackage?.Id ?? packageIndex]) };
    packageFields.forEach((field) => { row[`ShipmentPackages_${field}`] = shipmentPackage?.[field] ?? null; });
    return row;
  });
});

const shipmentColumns: GridColDef[] = [
  { field: 'ShipmentId', headerName: 'Shipment Id', minWidth: 120 },
  { field: 'ShippingMethodName', headerName: 'Shipping Method', minWidth: 200 },
  { field: 'CreateDate', headerName: 'Create Date', type: 'dateTime', minWidth: 180 },
  { field: 'ShipmentPackages_TrackingNumber', headerName: 'Tracking Number', type: 'string', editable: true, minWidth: 200 },
  { field: 'ShipmentPackages_ReturnLabelTrackingNumber', headerName: 'Return Label Tracking Number', type: 'string', editable: true, minWidth: 240 },
  ...shipmentFields.filter((field) => !['ShipmentId', 'ShippingMethodName', 'CreateDate'].includes(field)).map((field): GridColDef => ({ field, headerName: fieldLabel(field), minWidth: 180 })),
  ...['AddressFrom', 'AddressTo'].flatMap((prefix) => addressFields.map((field): GridColDef => ({ field: `${prefix}_${field}`, headerName: `${prefix === 'AddressFrom' ? 'From' : 'To'} ${fieldLabel(field)}`, minWidth: 180 }))),
  ...packageFields.filter((field) => !['TrackingNumber', 'ReturnLabelTrackingNumber'].includes(field)).map((field): GridColDef => ({ field: `ShipmentPackages_${field}`, headerName: `Package ${fieldLabel(field)}`, minWidth: 180 })),
];

type ShipmentLabelActions = { onReprintLabel?: (row: any) => void; reprintingRowId?: string | null; disabled?: boolean; trackingRows?: any[]; onTrackingRowsChange?: (rows: any[]) => void; trackingDisabled?: boolean; trackingScope?: string };

export const useDispatchOrderShipmentsTable = (shipments: any, active: boolean, { onReprintLabel, reprintingRowId, disabled = false, trackingRows, onTrackingRowsChange, trackingDisabled = false, trackingScope }: ShipmentLabelActions = {}): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const rows = React.useMemo(() => trackingRows ?? getDispatchOrderShipmentRows(shipments), [shipments, trackingRows]);
  const columns: GridColDef[] = React.useMemo(() => [
    {
      field: 'reprintAddressLabel', headerName: 'Address Label', minWidth: 210, sortable: false, filterable: false, disableExport: true, hideable: false,
      renderCell: (params: GridRenderCellParams) => {
        const missingPackageId = params.row.ShipmentPackages_Id == null && params.row.ShipmentPackageCount > 1;
        return <Tooltip title={missingPackageId ? 'Reload the order to get package IDs before reprinting this label.' : ''} arrow>
          <span>
            <CustomButton title={reprintingRowId === params.row.ShipmentPackageRowId ? 'Reprinting…' : 'Reprint address label'} size="small" variant="outlined" icon={<PrintIcon />} disabled={disabled || !onReprintLabel || reprintingRowId != null || params.row.ShipmentId == null || !params.row.ShipmentPackageCount || missingPackageId} onClick={(event) => {
              event.stopPropagation();
              onReprintLabel?.(params.row);
            }} />
          </span>
        </Tooltip>;
      },
    },
    ...shipmentColumns.map((column) => column.editable ? { ...column, editable: !disabled && !trackingDisabled } : column),
  ], [onReprintLabel, reprintingRowId, disabled, trackingDisabled]);
  const initialTableSettings = useInitialTableSettings({ tableName: trackingScope ? `DispatchOrderShipmentsTable_${trackingScope}` : 'DispatchOrderShipmentsTable', idPath }, rows);
  const tableSettings = useCompleteTableSettings({ columns }, initialTableSettings);
  tableSettings.autoHeight = true;
  tableSettings.hideFooter = true;
  tableSettings.loading = false;

  // Keep the current rows when leaving the tab; let the shared grid handle cell edits.
  useEffect(() => {
    const api = tableSettings.apiRef?.current;
    if (!active || !api?.subscribeEvent || !onTrackingRowsChange) return;
    return api.subscribeEvent('rowsSet', () => {
      const currentRows: any[] = Array.from(api.getRowModels().values());
      if (currentRows.length !== rows.length) return;
      const previous = new Map(rows.map((row) => [row.ShipmentPackageRowId, row]));
      if (currentRows.some((row) => !previous.has(row.ShipmentPackageRowId))) return;
      if (currentRows.some((row) => ['ShipmentPackages_TrackingNumber', 'ShipmentPackages_ReturnLabelTrackingNumber'].some((field) => String(row[field] ?? '') !== String(previous.get(row.ShipmentPackageRowId)?.[field] ?? '')))) onTrackingRowsChange(currentRows);
    });
  }, [active, rows, tableSettings.apiRef, onTrackingRowsChange]);

  useEffect(() => {
    if (active) tableSettings.apiRef?.current?.setRows?.(rows);
  }, [rows, tableSettings.apiRef, active]);

  return [columns, tableSettings];
};
